/**
 * ============================================================================
 * ProgramCard2 — Mapper
 * ============================================================================
 * Purpose: Pure transform of card props → display fields.
 * No fetching, no hooks, no JSX.
 * ============================================================================
 */

import { isIndiaCountry } from "@/helpers/countryCode";
import { resolveBrochureUrl } from "../utils";
import type {
  CollabLogoKey,
  ProgramCard2DisplayModel,
  ProgramCard2Fees,
  ProgramCard2Props,
} from "../model/program-card2.types";

export function displayFee(
  fee: ProgramCard2Fees | undefined,
  country?: string,
): string {
  if (!fee || typeof fee !== "object") return "";
  if (isIndiaCountry(country)) {
    return fee.FeesInInr ? `INR ${fee.FeesInInr.replace("₹", "")}` : "";
  }
  return fee.FeesInUSD ?? "";
}

export function getVisibleCollabKeys(
  isCertificationCard: boolean,
  industryAligned: string | undefined,
  isSponsoredCertification: boolean | undefined,
  programType: string | undefined,
): CollabLogoKey[] {
  const keys: CollabLogoKey[] = [];

  if (!isCertificationCard) {
    if (industryAligned === "MEDAVERSITY") keys.push("meda");
    if (industryAligned === "PAYTM") keys.push("paytm");
    if (industryAligned === "TCS") keys.push("tcs");
    if (industryAligned === "HCL") keys.push("hcl");
    if (industryAligned === "KPMG") keys.push("kpmg");
    if (industryAligned === "RICS") keys.push("rics");
    if (industryAligned === "WOMEN") keys.push("women");
    if (industryAligned === "PMI") keys.push("pmi");
  } else {
    if (isSponsoredCertification) keys.push("kpmg");
    if (programType === "SOAI") keys.push("soai");
  }

  return keys;
}

export function mapProgramCard2Display(
  props: ProgramCard2Props,
): ProgramCard2DisplayModel {
  const {
    basicDetails,
    fees,
    duration,
    country,
    isCertificationCard = false,
    industryAligned,
    isSchoolOfAIList = false,
    isProgramList = false,
    isAffiliatePage = false,
    id = "",
    associatedPrograms = [],
    isProgramListPage = false,
    corporateOffer,
    discountedFeesInr,
  } = props;

  const imageUrl = basicDetails?.Image?.source?.data?.[0]?.attributes?.url;
  const brochureLink = resolveBrochureUrl(basicDetails);
  const urlKey = basicDetails?.url_key || "";
  const programHeading = basicDetails?.Heading || "";
  const programHeadingMob = basicDetails?.HeadingMobile;
  const courseStripText = basicDetails?.CourseStripText;
  const programType = basicDetails?.ProgramType;
  const isSponsoredCertification = basicDetails?.isSponsoredCertification;
  // Corporate cards (only Corporate V5 passes corporateOffer): a program with a
  // discount shows the offer; one without (0%) shows the program's regular price
  // in the same box instead of silently falling back to a plain card, so the
  // row stays visually consistent.
  const isCorporateCard = Boolean(corporateOffer?.name);
  const hasCorporateOffer = isCorporateCard && Boolean(discountedFeesInr);

  const originalInr = (fees as ProgramCard2Fees | undefined)?.FeesInInr
    ?.replace(/\*/g, "")
    .replace("₹", "")
    .trim();

  const amount = isCorporateCard
    ? originalInr
      ? `₹ ${originalInr}`
      : ""
    : isCertificationCard || isSchoolOfAIList
      ? `${fees ?? ""}`
      : displayFee(fees as ProgramCard2Fees | undefined, country);

  const displayName = isCertificationCard
    ? programHeading
    : programHeadingMob || programHeading;

  const visibleCollabKeys = getVisibleCollabKeys(
    !!isCertificationCard,
    industryAligned,
    isSponsoredCertification,
    programType,
  );

  const industryAlignedValue = !isCertificationCard
    ? industryAligned || ""
    : "";

  return {
    imageUrl,
    brochureLink,
    urlKey,
    programHeading,
    courseStripText,
    programType,
    amount,
    displayName,
    isSponsoredCertification,
    visibleCollabKeys,
    duration,
    isCertificationCard,
    industryAligned,
    isProgramList,
    isAffiliatePage,
    id,
    associatedPrograms,
    isProgramListPage,
    corporateOfferLabel: isCorporateCard
      ? `${corporateOffer?.name} OFFER`.toUpperCase()
      : undefined,
    discountedAmount: hasCorporateOffer ? discountedFeesInr : undefined,
    // 0% discount: show the program's regular price (same value as the
    // `amount` field above) instead of a "no offer" note. `amount` is only
    // empty when there's no INR fee data at all — keep the old message as a
    // last-resort fallback for that edge case.
    noOfferMessage:
      isCorporateCard && !hasCorporateOffer
        ? amount || "No offer available"
        : undefined,
    industryAlignedValue,
  };
}
